import UUID from 'uuid-js';
import { computed } from '@ember/object';
import { readOnly, alias, reads } from '@ember/object/computed';
import Enum from '@square/dashboard-utilities/utils/enum';
import QuantityUnit from '@square/dashboard-data/models/orders/quantity-unit';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import TokenPair from '@square/dashboard-data/models/squareup/common/token/token-pair';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
import DiningOptionLineItem from '@square/dashboard-items-data/models/invoices/cart/dining-option-line-item';
import ItemModifierListSnapshot from '@square/dashboard-items-data/models/invoices/cart/item-modifier-list-snapshot';
import ItemizationEvent from '@square/dashboard-items-data/models/invoices/cart/itemization-event';
import FeatureDetails from '@square/dashboard-items-data/models/invoices/cart/itemization-feature-details';
import MenuCategorySnapshot from '@square/dashboard-items-data/models/invoices/cart/menu-category-snapshot';
import Money from '@square/dashboard-money/models/money';
import NumberAsString from '@square/dashboard-items-data/models/attrs/number-as-string';
import DiscountLineItem from '@square/dashboard-items-data/models/invoices/cart/discount-line-item';
import FeeLineItem from '@square/dashboard-items-data/models/invoices/cart/fee-line-item';
import ItemModifierSnapshot from '@square/dashboard-items-data/models/invoices/cart/item-modifier-snapshot';
import ItemSnapshot from '@square/dashboard-items-data/models/invoices/cart/item-snapshot';
import ItemVariationSnapshot from '@square/dashboard-items-data/models/invoices/cart/item-variation-snapshot';
export const BackingType = new Enum(['UNKNOWN', 'CUSTOM_AMOUNT', 'ITEMS_SERVICE_ITEM_VARIATION', 'CUSTOM_ITEM_VARIATION']);
const ModifierOptionLineItemAmounts = ProtoBaseModel.extend({
  modifierOptionMoney: attr(MoneyProto),
  modifierOptionTimesQuantityMoney: attr(MoneyProto)
});
const ModifierOptionLineItemBackingDetails = ProtoBaseModel.extend({
  modifierList: attr(ItemModifierListSnapshot),
  backingModifierOption: attr(ItemModifierSnapshot)
});
const ModifierOptionLineItem = ProtoBaseModel.extend({
  amounts: attr(ModifierOptionLineItemAmounts),
  writeOnlyBackingDetails: attr(ModifierOptionLineItemBackingDetails),
  hideFromCustomer: attr(Boolean),
  modifierOptionLineItemTokenPair: attr(TokenPair),
  id: readOnly('writeOnlyBackingDetails.backingModifierOption.id'),
  listId: alias('writeOnlyBackingDetails.backingModifierOption.modifierList.id'),
  name: alias('writeOnlyBackingDetails.backingModifierOption.name'),
  // `#price` should default to zero, even if null
  price: computed('writeOnlyBackingDetails.backingModifierOption.price', function () {
    return this.writeOnlyBackingDetails?.backingModifierOption?.price || Money.zero();
  }),
  clone() {
    const modifierOptionLineItemClone = ModifierOptionLineItem.deserialize(ModifierOptionLineItem.serialize(this));
    modifierOptionLineItemClone.set('modifierOptionLineItemTokenPair', TokenPair.create({
      clientToken: UUID.create().toString()
    }));
    return modifierOptionLineItemClone;
  }
});
ModifierOptionLineItem.reopenClass({
  Amounts: ModifierOptionLineItemAmounts,
  BackingDetails: ModifierOptionLineItemBackingDetails,
  createFor(modifierOption) {
    const writeOnlyBackingDetails = ModifierOptionLineItemBackingDetails.create({
      modifierList: ItemModifierListSnapshot.createFor(modifierOption.modifierList),
      backingModifierOption: ItemModifierSnapshot.createFor(modifierOption)
    });
    return this.create({
      writeOnlyBackingDetails,
      modifierOptionLineItemTokenPair: TokenPair.create({
        clientToken: UUID.create().toString()
      })
    });
  }
});
const ItemVariationDetailsBackingDetails = ProtoBaseModel.extend({
  itemVariation: attr(ItemVariationSnapshot),
  measurementUnit: attr(QuantityUnit)
});
const ItemVariationDetails = ProtoBaseModel.extend({
  writeOnlyBackingDetails: attr(ItemVariationDetailsBackingDetails),
  displayVariationName: attr(Boolean),
  clone() {
    return ItemVariationDetails.deserialize(ItemVariationDetails.serialize(this));
  }
});
ItemVariationDetails.reopenClass({
  BackingDetails: ItemVariationDetailsBackingDetails
});
const SelectedOptions = ProtoBaseModel.extend({
  discounts: repeated(DiscountLineItem, {
    key: 'discount',
    defaultValue: []
  }),
  fees: repeated(FeeLineItem, {
    key: 'fee',
    defaultValue: []
  }),
  itemVariationDetails: attr(ItemVariationDetails),
  modifierOptions: repeated(ModifierOptionLineItem, {
    key: 'modifier_option',
    defaultValue: []
  }),
  diningOption: attr(DiningOptionLineItem),
  clone() {
    return SelectedOptions.create({
      discounts: this.discounts.map(discount => discount.clone()),
      fees: this.fees.map(fee => fee.clone()),
      itemVariationDetails: this.itemVariationDetails && this.itemVariationDetails.clone(),
      modifierOptions: this.modifierOptions.map(modifierOption => modifierOption.clone()),
      diningOption: this.diningOption && this.diningOption.clone()
    });
  }
});
SelectedOptions.reopenClass({
  ItemVariationDetails,
  ModifierOptionLineItem
});
const Configuration = ProtoBaseModel.extend({
  selectedOptions: attr(SelectedOptions),
  backingType: attr(BackingType),
  itemVariationPriceMoney: attr(MoneyProto),
  clone() {
    const configurationClone = Configuration.deserialize(Configuration.serialize(this));
    configurationClone.set('selectedOptions', this.selectedOptions && this.selectedOptions.clone());
    return configurationClone;
  }
});
Configuration.reopenClass({
  SelectedOptions
});
const AvailableOptionsModifierList = ProtoBaseModel.extend({
  modifierList: attr(ItemModifierListSnapshot),
  modifierOptions: repeated(ItemModifierSnapshot, {
    key: 'modifier_option',
    defaultValue: []
  })
});
const AvailableOptions = ProtoBaseModel.extend({
  modifierLists: repeated(AvailableOptionsModifierList, {
    key: 'modifier_list',
    defaultValue: []
  })
});
const BackingDetails = ProtoBaseModel.extend({
  item: attr(ItemSnapshot),
  category: attr(MenuCategorySnapshot),
  availableOptions: attr(AvailableOptions)
});
BackingDetails.reopenClass({
  AvailableOptions,
  AvailableOptionsModifierList
});
const ItemizationAmounts = ProtoBaseModel.extend({
  itemVariationPriceMoney: attr(MoneyProto),
  itemVariationPriceTimesQuantityMoney: attr(MoneyProto),
  discountMoney: attr(MoneyProto),
  taxMoney: attr(MoneyProto),
  totalMoney: attr(MoneyProto),
  grossSalesMoney: attr(MoneyProto)
});
const CartItemization = ProtoBaseModel.extend({
  quantity: attr(NumberAsString),
  customNote: attr(String),
  configuration: attr(Configuration),
  featureDetails: attr(FeatureDetails),
  writeOnlyBackingDetails: attr(BackingDetails),
  amounts: attr(ItemizationAmounts),
  events: repeated(ItemizationEvent, {
    key: 'event',
    defaultValue: []
  }),
  itemizationTokenPair: attr(TokenPair),
  grossSalesMoney: alias('amounts.grossSalesMoney'),
  itemId: reads('writeOnlyBackingDetails.item.id'),
  itemVariation: reads('configuration.selectedOptions.itemVariationDetails.writeOnlyBackingDetails.itemVariation'),
  itemVariationPriceMoney: reads('configuration.itemVariationPriceMoney'),
  name: reads('writeOnlyBackingDetails.item.name'),
  modifierOptions: reads('configuration.selectedOptions.modifierOptions'),
  itemDescription: readOnly('writeOnlyBackingDetails.item.description'),
  // This will be called during ProtoBaseModel deserialization
  // TODO(ryanly): Move this to a better location. Potentially the version shimmer
  load(...args) {
    this._super(...args);
    if (this.configuration?.backingType === 'CUSTOM_AMOUNT') {
      this.set('configuration.backingType', 'CUSTOM_ITEM_VARIATION');
      this.setProperties({
        customNote: '',
        writeOnlyBackingDetails: BackingDetails.create({
          item: ItemSnapshot.create({
            name: this.customNote
          })
        })
      });
    }
  },
  clone() {
    const itemizationClone = CartItemization.deserialize(CartItemization.serialize(this));
    itemizationClone.setProperties({
      configuration: this.configuration && this.configuration.clone(),
      events: this.events.map(event => event.clone()),
      itemizationTokenPair: TokenPair.create({
        clientToken: UUID.create().toString()
      })
    });
    return itemizationClone;
  }
}).reopenClass({
  ModifierAmounts: ModifierOptionLineItemAmounts,
  Amounts: ItemizationAmounts,
  BackingDetails,
  Configuration,
  createDefault(options) {
    return this.create({
      configuration: Configuration.create({
        selectedOptions: SelectedOptions.create()
      }),
      itemizationTokenPair: TokenPair.create({
        clientToken: UUID.create().toString()
      }),
      featureDetails: FeatureDetails.createDefault(),
      ...options
    });
  }
});
export default CartItemization;